import { z } from 'zod';

import { ApiError, apiErrorMessage } from '@/lib/api';
import { formatDate } from '@/lib/dates';
import type { ContractActivityEventModel } from '@/features/contracts/contract';
import type { PaymentHistoryRow } from './payment-history';
import type {
  PaymentMethodType,
  PaymentReversalFieldsModel,
  ResolvedPaymentStatusT,
} from './payment';

type Translate = (key: string, options?: Record<string, unknown>) => string;

export const REVERSAL_REASON_MIN = 5;
export const REVERSAL_REASON_MAX = 500;
export const CORRECTION_NOTE_MIN = 3;
export const CORRECTION_NOTE_MAX = 500;

export type ReversePaymentRequest = Readonly<{
  reason: string;
  /** The amount as the operator typed it, normalised to two decimals. */
  confirmAmount: string;
}>;

export type ResolveZaadCorrectionRequest = Readonly<{ note: string }>;

export const ZaadCorrectionFilterSchema = z.enum(['open', 'resolved']);
export type ZaadCorrectionFilter = z.infer<typeof ZaadCorrectionFilterSchema>;

// ── Amount confirmation ─────────────────────────────────────────

/** What the operator is asked to type: the amount at exactly two decimals, no symbol or grouping. */
export const confirmAmountText = (amount: number): string => amount.toFixed(2);

const TYPED_AMOUNT = /^\d+(\.\d{1,2})?$/;

/** The typed amount at two decimals, or null when it is not a plain amount with at most two decimals. */
export function normaliseTypedAmount(typed: string): string | null {
  const trimmed = typed.trim();
  if (!TYPED_AMOUNT.test(trimmed)) return null;
  return Number(trimmed).toFixed(2);
}

/** "25", "25.0" and "25.00" all confirm 25; "25.001" and "24.99" do not. */
export function amountConfirmationMatches(typed: string, amount: number): boolean {
  if (!Number.isFinite(amount) || amount <= 0) return false;
  const normalised = normaliseTypedAmount(typed);
  return normalised !== null && normalised === confirmAmountText(amount);
}

// ── Text rules ──────────────────────────────────────────────────

export const trimmedLength = (text: string): number => text.trim().length;

export function reversalReasonIsValid(reason: string): boolean {
  const length = trimmedLength(reason);
  return length >= REVERSAL_REASON_MIN && length <= REVERSAL_REASON_MAX;
}

export function correctionNoteIsValid(note: string): boolean {
  const length = trimmedLength(note);
  return length >= CORRECTION_NOTE_MIN && length <= CORRECTION_NOTE_MAX;
}

const ReversePaymentSchema = z.object({
  reason: z.string().trim(),
  confirmAmount: z.string().trim(),
});

export const createReversePaymentSchema = (t: Translate, amount: number) =>
  ReversePaymentSchema.extend({
    reason: z
      .string()
      .trim()
      .min(
        REVERSAL_REASON_MIN,
        t('payments.reversal.validation.reasonTooShort', { min: REVERSAL_REASON_MIN })
      )
      .max(
        REVERSAL_REASON_MAX,
        t('payments.reversal.validation.reasonTooLong', { max: REVERSAL_REASON_MAX })
      ),
    confirmAmount: z
      .string()
      .refine(
        (typed) => amountConfirmationMatches(typed, amount),
        t('payments.reversal.validation.amountMismatch')
      )
      .transform((typed) => normaliseTypedAmount(typed) ?? typed.trim()),
  });

const ResolveCorrectionSchema = z.object({ note: z.string().trim() });

export const createResolveCorrectionSchema = (t: Translate) =>
  ResolveCorrectionSchema.extend({
    note: z
      .string()
      .trim()
      .min(
        CORRECTION_NOTE_MIN,
        t('payments.zaadCorrection.validation.noteTooShort', { min: CORRECTION_NOTE_MIN })
      )
      .max(
        CORRECTION_NOTE_MAX,
        t('payments.zaadCorrection.validation.noteTooLong', { max: CORRECTION_NOTE_MAX })
      ),
  });

// ── Errors ──────────────────────────────────────────────────────

export const ReversalErrorCodeSchema = z.enum([
  'payment_not_found',
  'payment_not_completed',
  'payment_already_reversed',
  'payment_not_reversible',
  'contract_completed',
  'contract_cancelled',
  'contract_written_off',
  'amount_mismatch',
  'loan_service_unavailable',
  'loan_service_refused',
  'forbidden',
]);
export type ReversalErrorCode = z.infer<typeof ReversalErrorCodeSchema>;

const REVERSAL_ERROR_KEYS: Record<ReversalErrorCode, string> = {
  payment_not_found: 'payments.reversal.errors.notFound',
  payment_not_completed: 'payments.reversal.errors.notCompleted',
  payment_already_reversed: 'payments.reversal.errors.alreadyReversed',
  payment_not_reversible: 'payments.reversal.errors.notReversible',
  contract_completed: 'payments.reversal.errors.contractCompleted',
  contract_cancelled: 'payments.reversal.errors.contractCancelled',
  contract_written_off: 'payments.reversal.errors.contractWrittenOff',
  amount_mismatch: 'payments.reversal.errors.amountMismatch',
  loan_service_unavailable: 'payments.reversal.errors.loanServiceUnavailable',
  loan_service_refused: 'payments.reversal.errors.loanServiceRefused',
  forbidden: 'payments.reversal.errors.forbidden',
};

export function reversalErrorCode(error: unknown): ReversalErrorCode | null {
  if (!(error instanceof ApiError)) return null;
  const parsed = ReversalErrorCodeSchema.safeParse(error.errorCode);
  if (parsed.success) return parsed.data;
  if (error.statusCode === 403) return 'forbidden';
  if (error.statusCode === 404) return 'payment_not_found';
  if (error.statusCode === 503) return 'loan_service_unavailable';
  return null;
}

/** A friendly line for a failed reversal; loan-service's own refusal text is kept, since it names the cause. */
export function reversalErrorMessage(t: Translate, error: unknown): string | null {
  if (!error) return null;
  const code = reversalErrorCode(error);
  if (code === 'loan_service_refused') {
    const detail = apiErrorMessage(error).trim();
    return detail
      ? t('payments.reversal.errors.loanServiceRefusedWith', { detail })
      : t(REVERSAL_ERROR_KEYS.loan_service_refused);
  }
  if (code) return t(REVERSAL_ERROR_KEYS[code]);
  return apiErrorMessage(error, t('payments.reversal.errors.generic'));
}

export const CorrectionErrorCodeSchema = z.enum([
  'no_correction_required',
  'correction_already_resolved',
  'payment_not_found',
  'forbidden',
]);
export type CorrectionErrorCode = z.infer<typeof CorrectionErrorCodeSchema>;

const CORRECTION_ERROR_KEYS: Record<CorrectionErrorCode, string> = {
  no_correction_required: 'payments.zaadCorrection.errors.notRequired',
  correction_already_resolved: 'payments.zaadCorrection.errors.alreadyResolved',
  payment_not_found: 'payments.zaadCorrection.errors.notFound',
  forbidden: 'payments.zaadCorrection.errors.forbidden',
};

export function correctionErrorCode(error: unknown): CorrectionErrorCode | null {
  if (!(error instanceof ApiError)) return null;
  const parsed = CorrectionErrorCodeSchema.safeParse(error.errorCode);
  if (parsed.success) return parsed.data;
  if (error.statusCode === 403) return 'forbidden';
  if (error.statusCode === 404) return 'payment_not_found';
  return null;
}

export function correctionErrorMessage(t: Translate, error: unknown): string | null {
  if (!error) return null;
  const code = correctionErrorCode(error);
  if (code) return t(CORRECTION_ERROR_KEYS[code]);
  return apiErrorMessage(error, t('payments.zaadCorrection.errors.generic'));
}

// ── Eligibility and state ───────────────────────────────────────

type ReversiblePayment = Readonly<{
  status: ResolvedPaymentStatusT;
  method: PaymentMethodType | null;
}>;

/** What the caller knows about the owning contract; omit when it is not loaded. */
export type ReversalContractState = Readonly<{
  status?: string | null;
  writtenOffAt?: string | null;
}>;

const CLOSED_CONTRACT_STATUSES = new Set(['completed', 'cancelled']);

/** Only a completed cash payment a staff member recorded can be reversed. */
export function canReversePayment(
  payment: ReversiblePayment,
  hasReversePermission: boolean,
  contract?: ReversalContractState | null
): boolean {
  if (!hasReversePermission) return false;
  if (payment.status !== 'completed' || payment.method !== 'cash') return false;
  if (contract?.writtenOffAt) return false;
  if (contract?.status && CLOSED_CONTRACT_STATUSES.has(contract.status)) return false;
  return true;
}

export const isReversed = (
  payment: Readonly<{ status: ResolvedPaymentStatusT }> &
    Pick<PaymentReversalFieldsModel, 'reversedAt'>
): boolean => payment.status === 'reversed' || Boolean(payment.reversedAt);

export type ZaadCorrectionState = 'none' | 'open' | 'resolved';

export function zaadCorrectionState(
  payment: Pick<
    PaymentReversalFieldsModel,
    'zaadCorrectionRequired' | 'zaadCorrectionResolvedAt'
  >
): ZaadCorrectionState {
  if (!payment.zaadCorrectionRequired) return 'none';
  return payment.zaadCorrectionResolvedAt ? 'resolved' : 'open';
}

/** "Corrected on … by … — note" for a resolved ZaadCredit correction. */
export function correctedSummary(
  t: Translate,
  payment: Pick<
    PaymentReversalFieldsModel,
    'zaadCorrectionResolvedAt' | 'zaadCorrectionResolvedByName' | 'zaadCorrectionNote'
  >
): string {
  const base = t(
    payment.zaadCorrectionResolvedByName
      ? 'payments.zaadCorrection.correctedBy'
      : 'payments.zaadCorrection.corrected',
    {
      date: formatDate(payment.zaadCorrectionResolvedAt),
      name: payment.zaadCorrectionResolvedByName ?? '',
    }
  );
  return payment.zaadCorrectionNote ? `${base} — ${payment.zaadCorrectionNote}` : base;
}

export const showZaadReportedWarning = (
  payment: Pick<PaymentReversalFieldsModel, 'zaadReported'>
): boolean => payment.zaadReported === true;

/**
 * The status a Payment History row shows. A merged row takes loan-service's
 * status, which may still say completed after offerings reversed the payment.
 */
export function historyRowStatus(
  row: Pick<PaymentHistoryRow, 'status' | 'payment'>
): ResolvedPaymentStatusT {
  return row.payment && isReversed(row.payment) ? 'reversed' : row.status;
}

// ── Timeline ────────────────────────────────────────────────────

export const PAYMENT_REVERSED_EVENT = 'payment_reversed';

export const isPaymentReversedEvent = (event: ContractActivityEventModel): boolean =>
  event.toState === PAYMENT_REVERSED_EVENT;

const numberOrNull = (value: unknown): number | null => {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/** Amount, reason and payment of a `payment_reversed` event, from its fields or its `data`. */
export function paymentReversedEventDetail(event: ContractActivityEventModel): Readonly<{
  paymentId: string | null;
  amount: number | null;
  reason: string | null;
}> {
  const data = event.data ?? {};
  return {
    paymentId:
      event.paymentId ?? (typeof data.paymentId === 'string' ? data.paymentId : null),
    amount: event.amount ?? numberOrNull(data.amount),
    reason: event.message ?? (typeof data.reason === 'string' ? data.reason : null),
  };
}

/** Payments a `payment_reversed` event names, so their own timeline row reads "reversed", not "failed". */
export function reversedPaymentIds(
  events: readonly ContractActivityEventModel[] | undefined
): ReadonlySet<string> {
  const ids = new Set<string>();
  for (const event of events ?? []) {
    if (!isPaymentReversedEvent(event)) continue;
    const { paymentId } = paymentReversedEventDetail(event);
    if (paymentId) ids.add(paymentId);
  }
  return ids;
}

// ── Cache ───────────────────────────────────────────────────────

/** Every query a reversal or a resolved correction can change. Prefix keys. */
export function reversalQueryKeys(
  paymentId: string,
  contractId: string | null | undefined
): ReadonlyArray<readonly unknown[]> {
  const keys: Array<readonly unknown[]> = [
    ['payment', paymentId],
    ['payments'],
    ['contracts'],
    ['dashboard'],
  ];
  if (contractId) {
    keys.push(
      ['contract', contractId],
      ['contract-payments', contractId],
      ['contract-loan-payments', contractId],
      ['contract-schedule', contractId],
      ['contract-activity', contractId],
      ['contract-loan', contractId]
    );
  }
  return keys;
}
